import React, { useState, useEffect, useContext } from "react";
import ProgressModal from "../ProgressModal";
import NotificationTypeStep from "@/components/elements/schedulePush/createNotification/NotificationTypeStep";
import ScheduleStep from "@/components/elements/schedulePush/createNotification/ScheduleStep";
import NotificationTextStep from "@/components/elements/schedulePush/createNotification/NotificationTextStep";
import ReviewStep from "@/components/elements/schedulePush/createNotification/ReviewStep";
import { SchedulePushNotificationContext } from "@/context/SchedulePushNotificationContext";
import {
  ICreateSchedulePush,
  IPublishedDetails,
  ScheduleType,
} from "@/interfaces/ScheduledNotifications";
import { getAppGuid } from "@/components/elements/search/utility";
import useHTTP from "@/components/hooks/useHTTP";
import "../../../css/global/progressModal/createNotification.scss";
import { formatDateWithOffset } from "@/helpers/Dates";

interface CreateNotificationModalProps {
  contentId: number;
  publishDetails: IPublishedDetails;
  updateNotificationId?: number;
  onCreateNotificationClick: any;
  setIsMidEditNotification?: any;
  hasMetNotificationLimit?: boolean;
}

const CreateNotificationModal = ({
  contentId,
  publishDetails,
  updateNotificationId,
  onCreateNotificationClick,
  setIsMidEditNotification,
  hasMetNotificationLimit,
}: CreateNotificationModalProps) => {
  const [stepsComplete, setStepsComplete] = useState<boolean[]>([
    true,
    false,
    false,
    false,
  ]);
  const [stepOneValid, setStepOneValid] = useState<boolean>(false);
  const [stepTwoValid, setStepTwoValid] = useState<boolean>(false);
  const [stepThreeValid, setStepThreeValid] = useState<boolean>(false);
  const [isFormValid, setFormValid] = useState<boolean>(false);
  const [isFirstStep, setIsFirstStep] = useState<boolean>(true);
  const [loading, setIsLoading] = useState<boolean>(false);
  const [stepAccess, setStepAccess] = useState<number>(null);
  const [submitHasBeenClicked, setSubmitHasBeenClicked] =
    useState<boolean>(false);

  const { get } = useHTTP();
  const { store, setStore } = useContext(SchedulePushNotificationContext);

  //setting store data
  useEffect(() => {
    const getNotificationData = async (notificationId: number) => {
      setIsLoading(true);
      try {
        const response = await get(
          `notifications/notification/${notificationId}`
        );
        const { data } = response;
        setStore(data);
      } catch (error) {
        console.error((error as Error)?.message);
      } finally {
        setIsLoading(false);
      }
    };

    if (updateNotificationId != null) {
      getNotificationData(updateNotificationId);
    }
  }, []);

  //logic for displaying view-continue-editing-modal after step one
  useEffect(() => {
    if (!isFirstStep) {
      setIsMidEditNotification(true);
    }
  }, [isFirstStep]);

  //setting store data
  useEffect(() => {
    if (stepOneValid && stepTwoValid && stepThreeValid && isFormValid) {
      setStepsComplete([true, true, true, true]);
      setStepAccess(null);
    } else if (stepOneValid && stepTwoValid && stepThreeValid) {
      setStepsComplete([true, true, true, true]);
      setStepAccess(3);
    } else if (stepOneValid && stepTwoValid) {
      setStepsComplete([true, true, true, false]);
      setStepAccess(2);
    } else if (stepOneValid) {
      setStepsComplete([true, true, false, false]);
      setStepAccess(1);
    } else {
      setStepAccess(0);
      setStepsComplete([true, false, false, false]);
    }
  }, [stepOneValid, stepTwoValid, stepThreeValid]);

  const onSubmitClick = () => {
    if (!submitHasBeenClicked) {
      let date;
      //setting correct date values at the time of request click:
      if (store.scheduleType === ScheduleType.NOW) {
        date = new Date();
      } else if (store.scheduleType === ScheduleType.PUBLISH) {
        date = new Date(publishDetails.scheduledPublishDate);
      } else {
        date = new Date(store.scheduledPushTime);
      }
      
      const scheduledPushTime = formatDateWithOffset(date);
      const requestObj: ICreateSchedulePush = {
        id: store.id,
        notificationType: store.notificationType,
        scheduledPushTime: scheduledPushTime,
        contentId: contentId,
        listId: store.listId,
        message: store.message,
        reach: store.reach,
        appGuid: getAppGuid(),
      };
      onCreateNotificationClick(requestObj);
      setSubmitHasBeenClicked(true);
    }
  };

  return (
    <>
      <div className="create-notification-modal-title">Create Notification</div>
      {!loading && (
        <ProgressModal
          navigation={[
            "Notification Type",
            "Schedule",
            "Notification Text",
            "Review",
          ]}
          steps={[
            <NotificationTypeStep
              key={"step1"}
              setStepOneValid={setStepOneValid}
            />,
            <ScheduleStep
              key={"step2"}
              setStepTwoValid={setStepTwoValid}
              publishDetails={publishDetails}
              hasMetNotificationLimit={hasMetNotificationLimit ?? false}
            />,
            <NotificationTextStep
              key={"step3"}
              setStepThreeValid={setStepThreeValid}
            />,
            <ReviewStep
              key={"step4"}
              setFormValid={setFormValid}
              publishDetails={publishDetails}
            />,
          ]}
          stepsComplete={stepsComplete}
          validation={[stepOneValid, stepTwoValid, stepThreeValid, isFormValid]}
          stepsAccess={stepAccess}
          isFormValid={isFormValid}
          submitAction={onSubmitClick}
          submitButtonText={
            updateNotificationId ? "Update" : "Create Notification"
          }
          submitHasBeenClicked={submitHasBeenClicked}
          setIsFirstStep={setIsFirstStep}
        />
      )}
    </>
  );
};

export default CreateNotificationModal;
